import {
  CSSProperties,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../redux/store";
import SearchInput from "../../../components/SearchInput";
import { Button } from "../../../components/button/Button";
import { ModalContext } from "../../../components/Modal";
import {
  fetchMembersCount,
  fetchMembersOfPage,
} from "../../../redux/queryMetaSlice";
import Checkbox from "../../../components/checkbox/Checkbox";
import {
  ColumnMenuModule,
  ColumnsToolPanelModule,
  ContextMenuModule,
  CellStyleModule,
  FiltersToolPanelModule,
  SetFilterModule,
} from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import {
  ClientSideRowModelModule,
  DateFilterModule,
  ModuleRegistry,
  NumberFilterModule,
  TextFilterModule,
  ValidationModule,
  RowSelectionModule,
  RowSelectionOptions,
  IDatasource,
  GridReadyEvent,
} from "ag-grid-community";
import useAgGridTheme from "../../../gridUtils/agGridTheme";
import {
  DEFAULT_COL_DEF,
  DEFAULT_GRID_OPTIONS,
} from "../../../gridUtils/gridUtils";
import {
  defaultFilterSelections,
  FilterBoxProps,
  FilterOption,
  TimeFilterOption,
  TimeCategoryOption,
} from "../../../interfaces/queryBuilderInterfaces";

import useRowSelection from "../../../gridUtils/useRowSelection";

ModuleRegistry.registerModules([
  RowSelectionModule,
  ClientSideRowModelModule,
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  ColumnMenuModule,
  ContextMenuModule,
  CellStyleModule,
  SetFilterModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  ValidationModule,
]);

const getNormalizedSearchTerms = (value: string) =>
  value
    .split(",")
    .map((item) => item.trim().toLowerCase())
    .filter((item) => item.length > 0);

const arrangeSearchResults = (
  options: TimeFilterOption[],
  value: string,
): TimeFilterOption[] => {
  const searchTerms = getNormalizedSearchTerms(value);

  if (searchTerms.length === 0 || value.trim().length < 3) {
    return options;
  }

  const prefixMatches: TimeFilterOption[] = [];
  const containsMatches: TimeFilterOption[] = [];

  options.forEach((option) => {
    const caption = option.caption.toLowerCase();
    const isPrefixMatch = searchTerms.some((searchTerm) =>
      caption.startsWith(searchTerm),
    );
    const isContainsMatch =
      !isPrefixMatch &&
      searchTerms.some((searchTerm) => caption.includes(searchTerm));

    if (isPrefixMatch) {
      prefixMatches.push(option);
      return;
    }

    if (isContainsMatch) {
      containsMatches.push(option);
    }
  });

  return [...prefixMatches, ...containsMatches];
};

const FilterBoxInfinite = ({
  treeData,
  filterSelections,
  applyFilter,
  advancedFilterGridRef,
  advancedFilterGridColumn,
  handleCheckAll,
  handleUncheckAll,
  onRowSelected,
  onCellClicked,
  selectRowsByArray,
  currentSelections,
  isExclude,
  setExclude,
  activePanelTab,
}: FilterBoxProps) => {
  const [filterOptions, setFilterOptions] = useState<TimeFilterOption[]>([]);
  const [selectedCategoryGroup, setSelectedCategoryGroup] =
    useState<string>("");
  const [isShowSelected, setShowSelected] = useState(false);
  const [isLoading, setLoading] = useState(true);
  //const [isExclude, setInternalExclude] = useState(filterSelections.exclude);
  const [style, setStyle] = useState<CSSProperties>({ height: "220px" });
  const dispatch: AppDispatch = useDispatch();
  const modalContext = useContext(ModalContext);
  const close = modalContext ? modalContext.close : () => {};
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [applyTo, setApplyTo] = useState<string | null>(null);
  const menuOptions = ["Rows", "Columns", "Filters"];
  const defaultOption = treeData.name === "Time Period" ? "Columns" : "Rows";
  const [showError, setShowError] = useState(false);

  const [showInfiniteGrid, setShowInfiniteGrid] = useState(true);

  const [searchText, setSearchText] = useState("");
  const currentSelectionsRef = useRef(currentSelections);
  useEffect(() => {
    currentSelectionsRef.current = currentSelections;
  }, [currentSelections]);

  const rowSelection = useMemo<
    RowSelectionOptions | "single" | "multiple"
  >(() => {
    return { mode: "multiRow" };
  }, []);

  const toggleMenu = () => {
    setShowMenu(!showMenu);
  };

  const excludeHandler = () => {
    if (setExclude) {
      setExclude(!isExclude);
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
      setShowMenu(false);
    }
  };

  const showSelectedHandler = () => {
    handleShowSelectedInternal(
      !isShowSelected,
      undefined,
      undefined,
      searchText,
    );
    setShowSelected(!isShowSelected);
  };

  const search = async (value: string) => {
    if (isShowSelected) {
      handleShowSelectedInternal(true, undefined, undefined, value);
      setSearchText(value);
      return;
    }
    if (value === "") {
      setShowInfiniteGrid(true);
      setSearchText("");
      setLoading(false);
      return;
    }

    setLoading(true);
    setShowInfiniteGrid(false);
    setSearchText(value);

    const data = await dispatch(
      fetchMembersOfPage({
        uniquename: treeData.uniquename ?? "",
        start: 0,
        end: 0,
        searchText: value,
      }),
    ).unwrap();

    setFilterOptions(arrangeSearchResults(data, value));
    setLoading(false);
  };

  const [selectedCategory, setSelectedCategory] =
    useState<TimeCategoryOption>();
  const [selectedSubCategory, setSelectedSubCategory] =
    useState<TimeCategoryOption>();

  const onGridReady = async (params: GridReadyEvent) => {
    const totalMembers = treeData.memberCount;

    params.api.setGridOption("datasource", { getRows: () => {} }); // Clear previous datasource

    const dataSource: IDatasource = {
      rowCount: undefined,
      getRows: async (params) => {
        advancedFilterGridRef.current?.api?.setGridOption("loading", true);
        const data = await dispatch(
          fetchMembersOfPage({
            uniquename: treeData.uniquename ?? "",
            start: params.startRow,
            end: params.endRow,
            searchText: searchText,
          }),
        ).unwrap();

        let lastRow = -1;
        if (data.length <= params.endRow) {
          lastRow = data.length;
        }

        params.successCallback(data, totalMembers);
        selectRowsByArray(currentSelectionsRef.current);
        advancedFilterGridRef.current?.api?.setGridOption("loading", false);
      },
    };
    params.api.setGridOption("datasource", dataSource);
  };

  useEffect(() => {
    if (
      isLoading &&
      advancedFilterGridRef &&
      advancedFilterGridRef.current &&
      advancedFilterGridRef.current.api
    ) {
      advancedFilterGridRef.current.api.showLoadingOverlay();
    } else if (
      advancedFilterGridRef &&
      advancedFilterGridRef.current &&
      advancedFilterGridRef.current.api
    ) {
      advancedFilterGridRef.current.api.hideOverlay();
    }

    if (
      isLoading &&
      advancedFilterGridRef &&
      advancedFilterGridRef.current &&
      advancedFilterGridRef.current.api
    ) {
      advancedFilterGridRef.current.api.showLoadingOverlay();
    } else if (
      advancedFilterGridRef &&
      advancedFilterGridRef.current &&
      advancedFilterGridRef.current.api
    ) {
      advancedFilterGridRef.current.api.hideOverlay();
    }
  }, [isLoading]);

  useEffect(() => {
    if (
      currentSelections &&
      advancedFilterGridRef &&
      advancedFilterGridRef.current &&
      advancedFilterGridRef.current.api
    ) {
      selectRowsByArray(currentSelections);
    }
    if (filterSelections) {
      //setExclude(filterSelections.exclude);
      setApplyTo(filterSelections.applyTo);
    }
  }, [filterOptions, currentSelections]);

  useEffect(() => {
    if (showMenu) {
      document.addEventListener("click", handleClickOutside);
    } else {
      document.removeEventListener("click", handleClickOutside);
    }
  }, [showMenu]);

  const handleShowSelectedInternal = (
    flag: boolean,
    selectedOption1: TimeCategoryOption | undefined,
    selectedOption2: TimeCategoryOption | undefined,
    searchVal: string,
  ) => {
    if (flag) {
      setFilterOptions(currentSelections);
      if (searchVal !== "") {
        const searchItems = getNormalizedSearchTerms(searchVal);
        let filteredOptions = currentSelections;
        if (searchItems.length > 0) {
          filteredOptions = arrangeSearchResults(
            filteredOptions.filter(
              (item) =>
                searchItems.some((searchItem) =>
                  item.caption.toLowerCase().includes(searchItem),
                ) &&
                (selectedCategory === undefined ||
                  selectedCategory?.isMember(item)) &&
                (selectedSubCategory === undefined ||
                  selectedSubCategory?.isMember(item)),
            ),
            searchVal,
          );
        }
        setFilterOptions(filteredOptions);
      }
      setShowInfiniteGrid(false);
    } else {
      setShowInfiniteGrid(true);
    }

    selectRowsByArray(currentSelections);
  };

  return (
    <div className="filter-dialog-container dialog-box-min filter-box">
      <div className="dialog-body nopadding">
        <div>
          <div className="search-filter">
            <SearchInput
              callback={search}
              debounce={300}
              placeholder="Search"
            />
          </div>

          <div className="filter-options-section">
            <div className="filter-checkbox-section">
              <label className="radio-inline" htmlFor="show-selected-radio">
                <Checkbox
                  id="show-selected-radio"
                  name="show-selected"
                  checked={isShowSelected}
                  onChange={showSelectedHandler}
                  value={""}
                  styleClass={""}
                  color={""}
                />
                <span className="ellipsis">Show Selected</span>
              </label>
              {treeData.name !== "Time Period" && (
                <label className="radio-inline" htmlFor="excluded-radio">
                  <Checkbox
                    id="excluded-radio"
                    name="exclude"
                    checked={!!isExclude}
                    onChange={excludeHandler}
                    value={""}
                    styleClass={""}
                    color={""}
                  />
                  <span className="ellipsis">Exclude</span>
                </label>
              )}
            </div>

            <div className="filter-button-section">
              <Button
                disabled={true}
                color="primary"
                className="btn md"
                variant="plain"
                id="btn-apply"
                onClick={() => {}}
              >
                Select All
              </Button>
              <Button
                color="primary"
                className="btn md"
                variant="plain"
                id="btn-apply"
                onClick={handleUncheckAll}
              >
                Deselect All
              </Button>
            </div>
          </div>

          <div className="ag-theme-alpine grid-wrapper grid-multi-select">
            {showInfiniteGrid && (
              <div style={style}>
                <AgGridReact<TimeFilterOption>
                  theme={useAgGridTheme("compact")}
                  ref={advancedFilterGridRef}
                  rowModelType={"infinite"}
                  columnDefs={advancedFilterGridColumn}
                  defaultColDef={DEFAULT_COL_DEF}
                  rowSelection={rowSelection}
                  gridOptions={DEFAULT_GRID_OPTIONS}
                  onRowSelected={onRowSelected}
                  onCellClicked={onCellClicked}
                  suppressContextMenu={true}
                  headerHeight={0}
                  onGridReady={onGridReady}
                />
              </div>
            )}
            {!showInfiniteGrid && (
              <div style={style}>
                <AgGridReact<TimeFilterOption>
                  theme={useAgGridTheme("compact")}
                  ref={advancedFilterGridRef}
                  rowData={filterOptions}
                  columnDefs={advancedFilterGridColumn}
                  defaultColDef={DEFAULT_COL_DEF}
                  rowSelection={rowSelection}
                  gridOptions={DEFAULT_GRID_OPTIONS}
                  onRowSelected={onRowSelected}
                  onCellClicked={onCellClicked}
                  suppressContextMenu={true}
                  headerHeight={0}
                  onGridReady={(params) => {
                    if (isLoading) {
                      params.api.showLoadingOverlay();
                    } else {
                      params.api.hideOverlay();
                    }
                    selectRowsByArray(currentSelections);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      {showError && (
        <div style={{ fontSize: "12px", color: "red" }}>
          Must select at least one filter value for this field
        </div>
      )}
      {/*currentSelections && currentSelections.length > 0 && 
                <div className="selected-filters">  
                    <span>Selection: {isExclude ? `Excluding ${currentSelections.length} value(s)` : `Including ${currentSelections.length} value(s)`}</span>
                </div>
            */}
    </div>
  );
};

export default FilterBoxInfinite;
